import { useState } from 'react';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  backLinkVariants,
  buttonVariants,
  Card,
  ConfirmDialog,
  EmptyState,
  HeroActions,
  HeroBar,
  HeroBody,
  HeroMeta,
  HeroTitle,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageBody,
  Spinner,
  Tab,
  TabList,
  Toast,
} from '@wundo/ui';
import {
  ChevronLeft,
  Clock,
  EllipsisVertical,
  MessageSquare,
  Pencil,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { ApiError } from '@wundo/client/api.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { startImpersonation } from '@/lib/impersonation.ts';
import {
  deleteUser,
  getUser,
  resubscribeToPlatformEmail,
  type UserRecord,
} from '@/lib/users.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { useRecordOrganisation } from '@/lib/recordOrganisation.ts';
import { type HashTab, useHashTabs } from '@/lib/useHashTabs.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import { MemoryList } from '@/components/users/MemoryList.tsx';
import { UserChatSessionsTab } from '@/components/users/UserChatSessionsTab.tsx';
import { UserCohortTab } from '@/components/users/UserCohortTab.tsx';
import { UserInfoTab } from '@/components/users/UserInfoTab.tsx';
import { UserSkillsTab } from '@/components/users/UserSkillsTab.tsx';

export const Route = createFileRoute('/_app/users/$id/')({
  staticData: { orgScope: 'record', orgScopeList: '/users' },
  component: UserDetailRoute,
});

function UserDetailRoute() {
  return <UserDetailPage id={Route.useParams().id} />;
}

/** One person, everything the console knows about them. */
export function UserDetailPage({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const superadmin = currentUser?.role === 'superadmin';
  const adminOrAbove =
    currentUser?.role === 'superadmin' || currentUser?.role === 'admin';

  const {
    data: user,
    isPending,
    error: loadError,
  } = useQuery({
    queryKey: ['users', 'detail', id],
    queryFn: () => getUser(id),
    retry: false,
  });
  useRecordOrganisation(user?.organisation_id);

  const resubscribe = useMutation({
    mutationFn: () => resubscribeToPlatformEmail(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users', 'detail', id] }),
  });

  const loginAs = useMutation({ mutationFn: () => startImpersonation(id) });

  if (isPending) {
    return (
      <PageBody className='flex justify-center'>
        <Spinner />
      </PageBody>
    );
  }

  if (loadError) {
    /* Not found is a state this page can draw; anything else is the shell's. */
    if (!(loadError instanceof ApiError) || loadError.status !== 404) throw loadError;

    return (
      <PageBody flush>
        <Card>
          <EmptyState
            title='User not found'
            description='It may have been deleted since this link was made.'
            action={
              <Link
                to='/users'
                className={buttonVariants({ size: 'sm', variant: 'quiet' })}
              >
                Back to users
              </Link>
            }
          />
        </Card>
      </PageBody>
    );
  }

  const name = `${user.first_name} ${user.last_name}`;

  return (
    <>
      <Link to='/users' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Users
      </Link>

      <PageBody flush className='flex flex-col gap-5'>
        <HeroBar>
          <HeroBody>
            <HeroTitle>{name}</HeroTitle>
            <HeroMeta>{user.organisation?.name ?? user.email}</HeroMeta>
          </HeroBody>
          <HeroActions>
            <UserActionsMenu
              user={user}
              superadmin={superadmin}
              adminOrAbove={adminOrAbove}
              onLoginAs={() => loginAs.mutate()}
              onDelete={() => setConfirmingDelete(true)}
            />
          </HeroActions>
        </HeroBar>

        <UserDetailTabs
          user={user}
          superadmin={superadmin}
          onResubscribe={() => resubscribe.mutate()}
          resubscribing={resubscribe.isPending}
        />
      </PageBody>

      <DeleteUserDialog
        user={user}
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      />

      {/* "Login as" reloads the page when it works, so this only ever says it did not. */}
      <Toast open={loginAs.isError} message='Failed to sign in as this user' />
      <Toast open={resubscribe.isError} message='Failed to resubscribe this person' />
    </>
  );
}

interface UserActionsMenuProps {
  user: UserRecord;
  superadmin: boolean;
  /** Whether the reader may edit and delete people at all. */
  adminOrAbove: boolean;
  onLoginAs: () => void;
  onDelete: () => void;
}

/** What can be done to one person, offered only where the backend would allow it. */
function UserActionsMenu({
  user,
  superadmin,
  adminOrAbove,
  onLoginAs,
  onDelete,
}: UserActionsMenuProps) {
  const router = useRouter();
  /* The backend refuses an admin editing or deleting a superadmin. */
  const locked = user.role === 'superadmin' && !superadmin;
  const canChange = adminOrAbove && !locked;

  return (
    <Menu>
      <MenuTrigger
        render={<IconButton aria-label='User actions' size='sm' variant='surface' />}
      >
        <EllipsisVertical className='size-4' aria-hidden='true' />
      </MenuTrigger>
      <MenuContent align='end'>
        {canChange && (
          <MenuItem
            onClick={() =>
              void router.navigate({ to: '/users/$id/edit', params: { id: user.id } })
            }
          >
            <Pencil aria-hidden='true' />
            Edit
          </MenuItem>
        )}
        {superadmin && (
          <MenuItem
            onClick={() => void router.navigate({ href: `/messages?user=${user.id}` })}
          >
            <MessageSquare aria-hidden='true' />
            Messages
          </MenuItem>
        )}
        {/* No superadmin or guest account may be signed in as, so it is not offered for one. */}
        {superadmin && user.role !== 'superadmin' && !user.guest_of_user_id && (
          <MenuItem onClick={onLoginAs}>
            <UserCircle aria-hidden='true' />
            Login as
          </MenuItem>
        )}
        {superadmin && (
          <MenuItem
            onClick={() =>
              void router.navigate({
                href: `/admin/change-logs?entity_type=user&entity_id=${user.id}`,
              })
            }
          >
            <Clock aria-hidden='true' />
            Change Logs
          </MenuItem>
        )}
        {canChange && (
          <MenuItem tone='danger' onClick={onDelete}>
            <Trash2 aria-hidden='true' />
            Delete
          </MenuItem>
        )}
      </MenuContent>
    </Menu>
  );
}

interface UserDetailTabsProps {
  user: UserRecord;
  /** Only a superadmin gets past the first tab. */
  superadmin: boolean;
  onResubscribe: () => void;
  resubscribing: boolean;
}

/**
 * A person's record on tabs. Only the first is anyone's business but a
 * superadmin's: the rest read conversations, memories and the skill engine's
 * working, which the backend refuses to anybody else anyway.
 */
function UserDetailTabs({
  user,
  superadmin,
  onResubscribe,
  resubscribing,
}: UserDetailTabsProps) {
  const tabs: HashTab[] = [
    { key: 'info', label: 'User Information' },
    ...(superadmin
      ? [
          { key: 'tracks', label: 'Cohort' },
          { key: 'chat-sessions', label: 'Chat Sessions' },
          { key: 'skills', label: 'Skills' },
          { key: 'memories', label: 'Memories' },
        ]
      : []),
  ];
  const { active, select } = useHashTabs(tabs);

  return (
    <>
      <TabList>
        {tabs.map((tab) => (
          <Tab key={tab.key} active={active === tab.key} onClick={() => select(tab.key)}>
            {tab.label}
          </Tab>
        ))}
      </TabList>

      {/*
        The record is label-and-value rows: a reading column, where the other
        tabs are lists and transcripts that want the console width.
      */}
      {active === 'info' && (
        <ReadingColumn>
          <UserInfoTab
            user={user}
            onResubscribe={onResubscribe}
            resubscribing={resubscribing}
          />
        </ReadingColumn>
      )}
      {active === 'tracks' && superadmin && (
        <UserCohortTab userId={user.id} organisationId={user.organisation_id} />
      )}
      {active === 'chat-sessions' && superadmin && (
        <UserChatSessionsTab userId={user.id} />
      )}
      {active === 'skills' && superadmin && <UserSkillsTab userId={user.id} />}
      {active === 'memories' && superadmin && <MemoryList userId={user.id} />}
    </>
  );
}

function DeleteUserDialog({
  user,
  open,
  onClose,
}: {
  user: UserRecord;
  open: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  function close() {
    onClose();
    setError('');
  }

  const remove = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess: async () => {
      close();
      queryClient.removeQueries({ queryKey: ['users', 'detail', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await router.navigate({ to: '/users' });
    },
    onError: (caught: unknown) =>
      setError(apiErrorMessage(caught, 'Failed to delete user')),
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title='Delete user?'
      description={`Are you sure you want to delete user ${user.first_name} ${user.last_name}? This action cannot be undone.`}
      confirmLabel='Delete'
      cancelLabel='Cancel'
      variant='danger'
      error={error === '' ? undefined : error}
      onConfirm={() => remove.mutateAsync().catch(() => undefined)}
    />
  );
}
